// src/components/ProtectedRoute.jsx

import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Divider,
} from '@mui/material';
import {
  Lock as LockIcon,
  Home as HomeIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';

export function ProtectedRoute({ children, requiredPage, requiredPermission }) {
  const { isAuthenticated, canAccessPage, hasPermission } = useAuth();

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check page access
  if (requiredPage && !canAccessPage(requiredPage)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // If all checks pass, render children
  return children;
}

export function UnauthorizedPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            borderRadius: 4,
            p: { xs: 3, sm: 5 },
            textAlign: 'center',
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: '50%',
              bgcolor: '#ffebee',
              mb: 2,
            }}
          >
            <LockIcon sx={{ fontSize: 64, color: '#f44336' }} />
          </Box>

          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1 }}>
            Unauthorized Access
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#666', mb: 2, fontWeight: 600 }}>
            غیر مجاز رسائی
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
            You don't have permission to access this page.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled', mb: 3 }}>
            Please contact your administrator if you believe this is a mistake.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/dashboard')}
              sx={{
                bgcolor: '#ff6b35',
                '&:hover': { bgcolor: '#e55a2b' },
                borderRadius: 2,
                px: 3,
                py: 1.2,
                fontWeight: 700,
              }}
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={() => {
                logout();
                navigate('/login');
              }}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.2,
                borderColor: '#ff6b35',
                color: '#ff6b35',
                '&:hover': {
                  borderColor: '#e55a2b',
                  bgcolor: '#fff3f0',
                },
              }}
            >
              Logout / لاگ آؤٹ
            </Button>
          </Box>

          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 3 }}>
            Error 403: Forbidden
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default ProtectedRoute;